import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Seller } from '@1po/1po-bff-fe-spec/generated/user/model/Seller';
import { Dispatch } from 'redux';
import { RootState } from 'app/AppStore';
import { getUserBehaviourEnabled } from 'domains/appContext/AppContext.store';
import {
  getPossibleBuyersForDelegationSaga,
  getIsSessionActive,
  getPossibleDelegateSessionBuyers,
  getPossibleDelegateSessionSellersData,
  getPossibleSellersForDelegationSaga,
  resetDelegationSessionSaga,
} from 'domains/delegation';
import { isUserTokenInvalid } from 'domains/user/User.mapper';
import {
  fetchUserNotificationsRequest,
  fetchVehicleSearchHistorySubscriptionSaga,
  getDealerType,
  getTokenExpired,
  getTokenUser,
  getTradingProfile,
  getUserSellersRequestSaga,
  getUserSellersToChoose,
  getVehicleSearchHistory,
  setCurrentUserSaga,
  setUserProfileSearchStatus,
} from 'domains/user/User.store';
import { ERROR, NO_DATA, useUserInactivity } from 'utils';
import {
  getErrorNoConnectionToBESide,
  isCollectingConnected,
  isConnected,
  wsCollectingConnect,
  setWsConnectInitialized,
  wsConnect,
  wsInactivityDisconnect,
} from '../webSockets/WebSocket.store';

export const useFetchVehicleSearchHistorySubscription = () => {
  const dispatch = useDispatch();

  const tradingProfile = useSelector(getTradingProfile);
  const vehicleHistory = useSelector(getVehicleSearchHistory);

  useEffect(() => {
    if (tradingProfile && vehicleHistory.searchStatus === undefined) {
      dispatch(fetchVehicleSearchHistorySubscriptionSaga());
    }
  }, [dispatch, tradingProfile, vehicleHistory]);
};

export const fetchSellers = (dispatch: Dispatch, sellersToChooseFrom?: Seller[] | NO_DATA) => {
  if (!sellersToChooseFrom) {
    dispatch(getUserSellersRequestSaga());
  }
};

export const useFetchSellers = () => {
  const dispatch = useDispatch();
  const sellersToChooseFrom = useSelector(getUserSellersToChoose);

  useEffect(() => {
    fetchSellers(dispatch, sellersToChooseFrom);
  }, [dispatch, sellersToChooseFrom]);
};

export const useConnectToBE = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(getTokenUser);
  useEffect(() => {
    if (currentUser) {
      if (isUserTokenInvalid(currentUser)) {
        dispatch(setUserProfileSearchStatus(ERROR));
        return;
      }
      if (currentUser?.selectedProfile) {
        dispatch(wsConnect());
      }
    }
  }, [dispatch, currentUser]);
};

export const useConnectToUserBehaviourMonitoringBE = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(getTokenUser);
  const isWsConnected = useSelector(isConnected);
  const isCollectorConnected = useSelector(isCollectingConnected);
  const isUserBehaviourEnabled = useSelector(getUserBehaviourEnabled);

  useEffect(() => {
    if (
      currentUser?.selectedProfile &&
      isWsConnected &&
      !isCollectorConnected &&
      isUserBehaviourEnabled &&
      currentUser.id
    ) {
      dispatch(wsCollectingConnect());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isWsConnected, isCollectorConnected, isUserBehaviourEnabled]);
};

export const useDisconnectWSOnUserInactivity = () => {
  const dispatch = useDispatch();
  const isWsConnected = useSelector(isConnected);
  const disconnectUserOnTimeout = useCallback(() => {
    if (isWsConnected) {
      dispatch(wsInactivityDisconnect());
      dispatch(setWsConnectInitialized(false));
    }
  }, [dispatch, isWsConnected]);
  useUserInactivity(disconnectUserOnTimeout, 1800000);
};

export const useFetchLocalUserToken = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setCurrentUserSaga());
  }, [dispatch]);
};

export const useFetchSellersForDelegationSession = () => {
  const dispatch = useDispatch();
  const sellers = useSelector(getPossibleDelegateSessionSellersData);
  const isDelegationSessionActive = useSelector(getIsSessionActive);
  const isR1 = useSelector(getDealerType) === 'R1';

  useEffect(() => {
    if (isR1 && !sellers?.searchStatus && !isDelegationSessionActive) {
      dispatch(getPossibleSellersForDelegationSaga());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isDelegationSessionActive]);
};

export const useResetDelegateSessionOnLogout = () => {
  const dispatch = useDispatch();
  const isNotConnectedToBE = useSelector(getErrorNoConnectionToBESide);
  const tokenExpired = useSelector(getTokenExpired);
  const isDelegationSessionActive = useSelector(getIsSessionActive);

  useEffect(() => {
    if (isDelegationSessionActive && (isNotConnectedToBE || tokenExpired)) {
      dispatch(resetDelegationSessionSaga());
    }
    // eslint-disable-next-line
  }, [isNotConnectedToBE, tokenExpired]);
};

export const useFetchBuyersForSellersForDelegationSession = (sellerTresorCode: string | undefined) => {
  const dispatch = useDispatch();
  const buyers = useSelector((state: RootState) => getPossibleDelegateSessionBuyers(state, sellerTresorCode));

  useEffect(() => {
    if (sellerTresorCode && !buyers?.searchStatus) {
      dispatch(getPossibleBuyersForDelegationSaga(sellerTresorCode));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sellerTresorCode]);
};

export const useFetchUserNotifications = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchUserNotificationsRequest());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
